import { createFileRoute, type SearchSchemaInput } from "@tanstack/react-router";
import { VerkaufssucheSeite } from "@/components/VerkaufssucheSeite";
import {
  alsSearch,
  ausSearch,
  OHNE_LEERE_EINZELWERTE,
  type SucheFilter,
} from "@/lib/suche-filter";

/*
 * `/verkaufssuche`. Die erste Route dieser SPA mit Search-Params — und damit
 * die erste, die mehr tut als eine Komponente zu benennen.
 *
 * ═══ Was `validateSearch` hier leistet ════════════════════════════════════
 *
 * Es ist die Grenze zwischen „unvertraut" und „getypt". Was aus der URL kommt,
 * kann fehlen, doppelt vorkommen oder vom falschen Typ sein — ein Lesezeichen
 * aus dem Altsystem, eine von Hand getippte Adresse, ein alter Link aus einer
 * E-Mail. `ausSearch` macht daraus einen vollständigen `SucheFilter`: jedes
 * der neunzehn Felder ist danach da, unbekannte Parameter sind weg.
 *
 * Die Funktion steht in `lib/suche-filter.ts` und nicht hier, weil `/suche`
 * dieselbe Grenze zieht. Zwei Fassungen davon wären zwei Auslegungen derselben
 * URL — und die Links sollen zwischen beiden Sichten austauschbar sein.
 *
 * ═══ Warum `alsSearch` beim Schreiben dazwischensteht ════════════════════
 *
 * Weil der Filter VOLLSTÄNDIG ist und die URL es nicht sein soll. Ein direkt
 * hinausgereichter `SucheFilter` schriebe `?q=&bw=&sort=&page=` an jede
 * Adresse: `stringifySearch` lässt `undefined` weg, aber `""` behält es — dort
 * ist der leere Wert der bedeutungstragende `loc=`-Marker. `alsSearch` trifft
 * die Unterscheidung, die diese Kodierung selbst nicht treffen kann.
 *
 * Serialisiert wird danach vom Router mit `stringifySearch`/`parseSearch` aus
 * `main.tsx` — gegen `testdata/golden/search-params.json` gepinnt.
 *
 * Diese Auswahl allein HÄLT NICHT: der Router baut die Adresse beim Mount mit
 * `_includeValidateSearch` neu, und `validateSearch` setzt die weggelassenen
 * Felder wieder ein. Deshalb steht dieselbe Auswahl unten ein zweites Mal als
 * `search.middlewares` — die Begründung samt gemessener Gegenrichtung (der
 * `loc=`-Marker MUSS überleben) steht bei `OHNE_LEERE_EINZELWERTE`.
 *
 * ═══ Kein `loader`, kein `beforeLoad` ════════════════════════════════════
 *
 * Wie bei `controlling.tsx` und `totes-kapital.tsx`: die Daten holt der
 * Query-Hook der Komponente. Damit gehören Lade-, Fehler- und Zugangszustand
 * derselben Stelle, die sie auch anzeigt — und ein 401 landet nicht als
 * Router-Fehler neben der Seite, sondern als Aussage darin.
 */
export const Route = createFileRoute("/verkaufssuche")({
  component: VerkaufssucheRoute,
  /*
   * `& SearchSchemaInput` ist kein Zierrat, sondern der Schalter: nur mit
   * dieser Markierung im PARAMETERTYP führt TanStack Eingabe und Ausgabe
   * getrennt. Ohne sie leitet es beides aus dem Rückgabetyp ab, und ein
   * `navigate({ search })` müsste alle neunzehn Felder mitschicken —
   * gemessen: `Type 'SearchObjekt' is missing the following properties … g,
   * alt, loc, og, and 15 more`.
   *
   * Der Typ selbst trägt zur Laufzeit nichts; `ausSearch` nimmt weiterhin
   * jedes rohe Objekt entgegen und macht daraus einen vollständigen Filter.
   */
  validateSearch: (roh: Record<string, unknown> & SearchSchemaInput): SucheFilter =>
    ausSearch(roh),
  // Läuft NACH `validateSearch` und hat damit das letzte Wort über die
  // Adresse. Zeichengleich mit `/suche`, aus dem Grund, den der Kopf dieser
  // Datei für `validateSearch` schon nennt: zwei Auslegungen derselben URL
  // wären zwei Wahrheiten.
  search: { middlewares: [OHNE_LEERE_EINZELWERTE] },
});

function VerkaufssucheRoute() {
  const filter = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <VerkaufssucheSeite
      filter={filter}
      aufFilter={(neu) => {
        /*
         * `void`: `navigate` liefert ein Promise, auf das hier niemand wartet.
         * Das ist gewollt — die Navigation ist der Effekt, nicht ihr Abschluss.
         * Ohne die Markierung meldet `no-floating-promises` zu Recht.
         */
        void navigate({
          search: alsSearch(neu),
          replace: neu.page === "" || neu.page === filter.page,
          resetScroll: false,
        });
      }}
    />
  );
}
